import { useCallback } from 'react';

import useAccountQuery from 'api/queries/useAccountQuery';
import useFeatureRecordQuery, { FEATURE_KEYS, shouldSeeFeature } from 'api/queries/useFeatureRecordQuery';
import useSelectionsQuery from 'api/queries/useSelectionsQuery';
import { getFormattedMembershipDiscount } from 'features/paid-membership/ActivateMembershipPricing/utils';
import Tag from 'honeydew/Tag/Tag';
import { useAccountPlusMembershipHook } from 'hooks/useAccountPlusMembershipHook';
import useMarkFeatureSeen from 'hooks/useMarkFeatureSeen';
import { getCartMemberSavings, getDecimalDisplay, getFreeShippingThresholdMet, PriceType } from 'utils/priceUtils';

/** Minimum cart+shipping savings to show savings-based pill lines (dollars). */
export const PLUS_MEMBERSHIP_OPT_IN_SAVINGS_THRESHOLD = 5;

/**
 * Which copy path `getPlusMembershipOptInPillMessage` used (for analytics, tests, and
 * `data-plus-membership-opt-in-pill-message-variant`). Values match `PLUS_MEMBERSHIP_OPT_IN_PILL_MESSAGE_VARIANT`.
 */
export const PLUS_MEMBERSHIP_OPT_IN_PILL_MESSAGE_VARIANT = {
    CREDIT: 'credit',
    JOIN_SAVINGS: 'join_savings',
    LIMITED_TIME_DISCOUNT_NO_TRIAL: 'limited_time_discount_no_trial',
    LIMITED_TIME_DISCOUNT_WITH_TRIAL: 'limited_time_discount_with_trial',
    TRY_FREE_SAVINGS: 'try_free_savings',
};

/**
 * Potential item + shipping savings if the customer joins M+, aligned with `PlusMembershipMemberBanner`.
 * Item savings use plus member line pricing vs default. Shipping savings apply whenever the cart is
 * below the free-shipping threshold at the same `priceType` used for checkout (`PLUS_MEMBER` when
 * `plusMembershipEligibleOrder`, else `DEFAULT`) and `shippingFees` reports a member shipping benefit
 * — including orders that are not `plusMemberPriceEligible` but still show member preview prices and
 * “join to waive shipping” copy.
 * @param {Object} p
 * @param {Object} p.account
 * @param {Object} p.selections
 * @param {boolean} p.plusMembershipEligibleOrder
 * @param {{ plusMembershipShippingSavings: number } | null | undefined} p.shippingFees
 * @returns {number}
 */
export function getPotentialPlusMembershipCartSavings({
    account,
    plusMembershipEligibleOrder,
    selections,
    shippingFees,
}) {
    const priceType = plusMembershipEligibleOrder ? PriceType.PLUS_MEMBER : PriceType.DEFAULT;
    const freeShippingThresholdMet = getFreeShippingThresholdMet({
        account,
        options: { priceType },
        selections,
    });
    const { plusMembershipShippingSavings = 0 } = shippingFees ?? {};
    const cartMemberShippingSavings =
        !freeShippingThresholdMet && plusMembershipShippingSavings > 0 ? plusMembershipShippingSavings : 0;
    return getCartMemberSavings({ account, selections }) + cartMemberShippingSavings;
}

/**
 * Resolves storefront M+ opt-in pill copy. Hierarchy (M- = not paid M+; FT = `availableTrialMembership`):
 * 1) M- + discount + FT offer → limited-time discount line
 * 2) M- + no FT + discount or credit → credit (if any) or limited-time discount
 * 3) FT + potential M+ (item+shipping) savings over the threshold → try free & save on this order
 * 4) no new-member offer + savings over the threshold → join & save
 * Any other case (incl. no new-member offer + savings at or below the threshold) → null. When null,
 * `showPlusMembershipOptInPill` is false. On success, returns `message` (a `span` with `data-plus-membership-opt-in-pill-message-variant`)
 * and `messageVariant` for analytics.
 * @param {Object} p
 * @param {Object} p.account
 * @param {Object} p.selections
 * @param {Object} p.plusData
 * @param {Object} [p2]
 * @param {function} [p2.formatDiscount] - For tests: mock discount formatter; defaults to `getFormattedMembershipDiscount`.
 * @param {function} [p2.getPotentialPlusMembershipSavings] - For tests: override savings; defaults to `getPotentialPlusMembershipCartSavings`.
 * @returns {{ message: ReactNode, messageVariant: string } | null}
 */
export function getPlusMembershipOptInPillMessage(
    { account, plusData, selections },
    {
        formatDiscount = (o) => getFormattedMembershipDiscount(o),
        getPotentialPlusMembershipSavings: getSavings = getPotentialPlusMembershipCartSavings,
    } = {}
) {
    if (!account || !selections) {
        return null;
    }

    const {
        availableCredits,
        availableTrialMembership,
        isPlusMembershipOptionsLoading,
        membership,
        membershipStatus,
        newMemberOffer,
        plusMembershipEligibleOrder,
        plusMembershipShortBranding,
        shippingFees,
    } = plusData;

    if (isPlusMembershipOptionsLoading) {
        return null;
    }

    // No join copy for paid M+ (`membership` is set) or for anyone already in the program: active paid
    // (`membershipStatus` `active`) or M+ free trial (`trial` — `membership` stays null while on trial).
    if (membership || membershipStatus === 'active' || membershipStatus === 'trial') {
        return null;
    }

    // M- = not on paid M+ and not on an M+ trial; only `eligible` should reach the copy below.
    const isMMinusUser = !membership;

    const formattedDiscount = formatDiscount({ newMemberOffer });
    const hasCreditOffer = (availableCredits?.credits ?? 0) > 0;
    const hasDiscountOffer = Boolean(formattedDiscount);

    const potentialPlusMembershipSavings = getSavings({
        account,
        selections,
        plusMembershipEligibleOrder,
        shippingFees,
    });
    const savingsDisplay = getDecimalDisplay(potentialPlusMembershipSavings, true) || '0.00';
    const minSavings = PLUS_MEMBERSHIP_OPT_IN_SAVINGS_THRESHOLD;
    const hasNewMemberOffer = Boolean(availableTrialMembership || hasCreditOffer || hasDiscountOffer);
    const creditDollarsDisplay = getDecimalDisplay(availableCredits?.credits, true) || '0.00';
    const brand = plusMembershipShortBranding;

    // 1. M- + membership discount + Free Trial offer: message discount.
    if (isMMinusUser && availableTrialMembership && hasDiscountOffer) {
        const messageVariant = PLUS_MEMBERSHIP_OPT_IN_PILL_MESSAGE_VARIANT.LIMITED_TIME_DISCOUNT_WITH_TRIAL;
        return {
            message: (
                <span data-plus-membership-opt-in-pill-message-variant={messageVariant}>
                    <b>Limited time: {formattedDiscount} off</b> an{' '}
                    <Tag bgColor="white" variant="dark" fontSize="mobileXXS">
                        <span className="text-cranberry if:text-blueberry">{brand}</span>
                    </Tag>{' '}
                    membership
                </span>
            ),
            messageVariant,
        };
    }

    // 2. M- + no FT, but discount or credit: message discount or credit (credit first when both).
    if (isMMinusUser && !availableTrialMembership && (hasCreditOffer || hasDiscountOffer)) {
        if (hasCreditOffer) {
            const messageVariant = PLUS_MEMBERSHIP_OPT_IN_PILL_MESSAGE_VARIANT.CREDIT;
            return {
                message: (
                    <span data-plus-membership-opt-in-pill-message-variant={messageVariant}>
                        <b>Get ${creditDollarsDisplay} in credits</b> when you sign up for{' '}
                        <Tag bgColor="white" variant="dark" fontSize="mobileXXS">
                            <span className="text-cranberry if:text-blueberry">{brand}</span>
                        </Tag>
                    </span>
                ),
                messageVariant,
            };
        }
        const messageVariant = PLUS_MEMBERSHIP_OPT_IN_PILL_MESSAGE_VARIANT.LIMITED_TIME_DISCOUNT_NO_TRIAL;
        return {
            message: (
                <span data-plus-membership-opt-in-pill-message-variant={messageVariant}>
                    <b>Limited time: {formattedDiscount} off</b> an{' '}
                    <Tag bgColor="white" variant="dark" fontSize="mobileXXS">
                        <span className="text-cranberry if:text-blueberry">{brand}</span>
                    </Tag>{' '}
                    membership
                </span>
            ),
            messageVariant,
        };
    }

    // 3. Free Trial offer + potential M+ savings (item + shipping) over threshold: FT and savings.
    if (availableTrialMembership && potentialPlusMembershipSavings > minSavings) {
        const messageVariant = PLUS_MEMBERSHIP_OPT_IN_PILL_MESSAGE_VARIANT.TRY_FREE_SAVINGS;
        return {
            message: (
                <span data-plus-membership-opt-in-pill-message-variant={messageVariant}>
                    Try{' '}
                    <Tag bgColor="white" variant="dark" fontSize="mobileXXS">
                        <span className="text-cranberry if:text-blueberry">{brand}</span>
                    </Tag>{' '}
                    for free & save <b>${savingsDisplay}</b> on this order
                </span>
            ),
            messageVariant,
        };
    }

    // 4. No new-member offer + savings over threshold: savings only.
    if (!hasNewMemberOffer && potentialPlusMembershipSavings > minSavings) {
        const messageVariant = PLUS_MEMBERSHIP_OPT_IN_PILL_MESSAGE_VARIANT.JOIN_SAVINGS;
        return {
            message: (
                <span data-plus-membership-opt-in-pill-message-variant={messageVariant}>
                    Join{' '}
                    <Tag bgColor="white" variant="dark" fontSize="mobileXXS">
                        <span className="text-cranberry if:text-blueberry">{brand}</span>
                    </Tag>{' '}
                    to save <b>${savingsDisplay}</b> on this order
                </span>
            ),
            messageVariant,
        };
    }

    return null;
}

/**
 * M+ opt-in pill: feature record (`plus_membership_opt_in_pill_viewed` falsy via `shouldSeeFeature`)
 * and derived CTA line. Dismiss calls `markAsSeen` with `includeChargeId: true` so the POST scopes
 * the mark to the open shopping window when `chargeID` is available.
 *
 * @returns {{
 *   dismissPlusMembershipOptInPill: () => void,
 *   plusMembershipOptInPillMessage: ReactNode | null,
 *   plusMembershipOptInPillMessageVariant: string | null,
 *   showPlusMembershipOptInPill: boolean,
 * }}
 */
export function usePlusMembershipOptInPill() {
    const { data: featureRecord } = useFeatureRecordQuery();
    const { markAsSeen } = useMarkFeatureSeen();

    const dismissPlusMembershipOptInPill = useCallback(() => {
        markAsSeen(FEATURE_KEYS.PLUS_MEMBERSHIP_OPT_IN_PILL_VIEWED, { includeChargeId: true });
    }, [markAsSeen]);

    const { data: account } = useAccountQuery();
    const { data: selections } = useSelectionsQuery();
    const {
        availableCredits,
        availableTrialMembership,
        isPlusMembershipOptionsLoading,
        membership,
        membershipStatus,
        newMemberOffer,
        plusMembershipEligibleOrder,
        plusMembershipShortBranding,
        shippingFees,
    } = useAccountPlusMembershipHook();

    const plusMembershipOptInPillPayload = getPlusMembershipOptInPillMessage({
        account: account || undefined,
        selections: selections || undefined,
        plusData: {
            isPlusMembershipOptionsLoading,
            membership,
            membershipStatus,
            newMemberOffer,
            availableTrialMembership,
            availableCredits,
            plusMembershipShortBranding,
            plusMembershipEligibleOrder,
            shippingFees,
        },
    });
    const plusMembershipOptInPillMessage = plusMembershipOptInPillPayload?.message ?? null;
    const plusMembershipOptInPillMessageVariant = plusMembershipOptInPillPayload?.messageVariant ?? null;

    const showPlusMembershipOptInPill =
        plusMembershipOptInPillMessage != null &&
        shouldSeeFeature(featureRecord, FEATURE_KEYS.PLUS_MEMBERSHIP_OPT_IN_PILL_VIEWED);

    return {
        dismissPlusMembershipOptInPill,
        plusMembershipOptInPillMessage,
        plusMembershipOptInPillMessageVariant,
        showPlusMembershipOptInPill,
    };
}
